import React, { useEffect, useState } from 'react';
import { BookOpen, Save, Image as ImageIcon } from 'lucide-react';
import { fetchAboutMission, updateAboutMission } from '../api/aboutMissionApi';
import { getImageUrl } from '../api/imageUtils';

const AboutMissionAdmin = () => {
  const [form, setForm] = useState({
    label: 'Our Mission',
    title: '',
    subtitle: '',
    bodyParagraphs: '',
    listItems: '',
    quoteText: '',
    quoteAuthor: '',
    isActive: true,
  });
  const [existingImage, setExistingImage] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchAboutMission()
      .then((data) => {
        if (!data) return;
        setForm({
          label: data.label || 'Our Mission',
          title: data.title || '',
          subtitle: data.subtitle || '',
          bodyParagraphs: (data.bodyParagraphs || []).join('\n\n'),
          listItems: (data.listItems || []).join('\n'),
          quoteText: data.quoteText || '',
          quoteAuthor: data.quoteAuthor || '',
          isActive: data.isActive,
        });
        setExistingImage(data.imagePath || '');
      })
      .catch((e) => setError(e.response?.data?.error || e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await updateAboutMission({
        label: form.label,
        title: form.title,
        subtitle: form.subtitle,
        body_paragraphs: form.bodyParagraphs,
        list_items: form.listItems,
        quote_text: form.quoteText,
        quote_author: form.quoteAuthor,
        is_active: form.isActive ? '1' : '0',
        file,
      });
      setSuccess('About mission section saved.');
      setFile(null);
      const refreshed = await fetchAboutMission();
      if (refreshed?.imagePath) setExistingImage(refreshed.imagePath);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const previewImage = file ? URL.createObjectURL(file) : getImageUrl(existingImage);

  if (loading) return <p className="text-slate-500 p-6">Loading…</p>;

  return (
    <div className="ui-page animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight text-gradient flex items-center gap-2">
          <BookOpen size={28} className="text-emerald-600" /> About — Our Mission
        </h1>
        <p className="text-slate-500 mt-1 text-sm">
          Edit the &quot;Our Mission&quot; section on the About page — image, text, quote and bullet list.
        </p>
      </div>

      {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>}
      {success && <div className="mb-4 p-3 rounded-lg bg-emerald-50 text-emerald-800 text-sm border border-emerald-200">{success}</div>}

      <form onSubmit={handleSave} className="ui-card ui-card-pad space-y-5 max-w-3xl">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="ui-label">Label</label>
            <input name="label" value={form.label} onChange={handleChange} className="ui-input w-full" />
          </div>
          <div>
            <label className="ui-label">Title *</label>
            <input name="title" value={form.title} onChange={handleChange} className="ui-input w-full" required />
          </div>
        </div>

        <div>
          <label className="ui-label">Subtitle</label>
          <textarea name="subtitle" value={form.subtitle} onChange={handleChange} rows={2} className="ui-input w-full" />
        </div>

        <div>
          <label className="ui-label">Body paragraphs (separate with blank line)</label>
          <textarea name="bodyParagraphs" value={form.bodyParagraphs} onChange={handleChange} rows={5} className="ui-input w-full" />
        </div>

        <div>
          <label className="ui-label">Bullet list (one item per line)</label>
          <textarea name="listItems" value={form.listItems} onChange={handleChange} rows={4} className="ui-input w-full" />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="ui-label">Image quote overlay</label>
            <input name="quoteText" value={form.quoteText} onChange={handleChange} className="ui-input w-full" />
          </div>
          <div>
            <label className="ui-label">Quote author</label>
            <input name="quoteAuthor" value={form.quoteAuthor} onChange={handleChange} className="ui-input w-full" />
          </div>
        </div>

        <div>
          <label className="ui-label flex items-center gap-2"><ImageIcon size={16} /> Section image</label>
          <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="ui-input w-full" />
          {previewImage && (
            <img src={previewImage} alt="" className="mt-3 h-40 rounded-lg object-cover border border-slate-200" />
          )}
        </div>

        <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} />
          Visible on About page
        </label>

        <button type="submit" disabled={saving} className="ui-btn ui-btn-primary inline-flex items-center gap-2">
          <Save size={16} /> {saving ? 'Saving…' : 'Save section'}
        </button>
      </form>
    </div>
  );
};

export default AboutMissionAdmin;
